import React, {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CheckCircleTwoTone, LoadingOutlined } from '@ant-design/icons';
import { Col, Form, Input, Row, Space, Typography } from 'antd';
import { useIntl } from 'react-intl';

import FloatingFormItem from '@/components/_common/FloatingFormItem';

export type RegisterAccountLoginValidationStatus =
  | 'idle'
  | 'validating'
  | 'valid'
  | 'invalid';

type LoginValidationResult = {
  isValid: boolean;
  reason?: string;
};

type RegisterAccountFormProps = {
  validateLogin?: (login: string) => Promise<LoginValidationResult>;
  loginValidationDebounceMs?: number;
  onLoginValidationChange?: (
    status: RegisterAccountLoginValidationStatus,
  ) => void;
};

const LOGIN_MIN_LENGTH = 5;
const LOGIN_MAX_LENGTH = 50;
const LOGIN_ALLOWED_CHARACTERS = /^[a-zA-Z0-9._@-]+$/;
const LOGIN_HELP_TEXT = '5 a 50 caracteres, sem espaços.';
const LOGIN_AVAILABLE_MESSAGE = 'Login disponível.';
const PASSWORD_HELP_TEXT =
  'Mínimo 8 caracteres, com maiúscula, minúscula e número.';

export default function RegisterAccountForm({
  validateLogin,
  loginValidationDebounceMs = 600,
  onLoginValidationChange,
}: RegisterAccountFormProps) {
  const intl = useIntl();
  const form = Form.useFormInstance();
  const requestIdRef = useRef(0);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const validateLoginRef = useRef(validateLogin);
  const loginValidationStatusRef =
    useRef<RegisterAccountLoginValidationStatus>('idle');
  const hasLoginValidation = !!validateLogin;
  const [loginValidationStatus, setLoginValidationStatus] =
    useState<RegisterAccountLoginValidationStatus>('idle');
  const [loginFeedback, setLoginFeedback] = useState<string>();

  useEffect(() => {
    validateLoginRef.current = validateLogin;
  }, [validateLogin]);

  useEffect(
    () => () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    },
    [],
  );

  const updateLoginValidationStatus = useCallback(
    (status: RegisterAccountLoginValidationStatus) => {
      if (loginValidationStatusRef.current === status) return;

      loginValidationStatusRef.current = status;
      setLoginValidationStatus(status);
      onLoginValidationChange?.(status);
    },
    [onLoginValidationChange],
  );

  const clearLoginFeedback = useCallback(() => {
    setLoginFeedback((currentFeedback) =>
      currentFeedback ? undefined : currentFeedback,
    );
  }, []);

  function getLoginValue(value: unknown) {
    return typeof value === 'string' ? value : '';
  }

  function getLoginFeedbackType(status: RegisterAccountLoginValidationStatus) {
    if (status === 'valid') return 'success';
    if (status === 'invalid') return 'danger';
    return 'secondary';
  }

  function hasLoginMinLength(value: unknown) {
    return getLoginValue(value).trim().length >= LOGIN_MIN_LENGTH;
  }

  function getLoginValidationError(value: unknown) {
    const login = getLoginValue(value);

    if (!login.trim()) return undefined;

    if (/\s/.test(login)) {
      return 'Não pode conter espaços.';
    }

    if (!LOGIN_ALLOWED_CHARACTERS.test(login)) {
      return 'Use apenas letras, números, ponto, hífen, underline ou @.';
    }

    if (login.length < LOGIN_MIN_LENGTH || login.length > LOGIN_MAX_LENGTH) {
      return `Deve ter entre ${LOGIN_MIN_LENGTH} e ${LOGIN_MAX_LENGTH} caracteres.`;
    }

    return undefined;
  }

  function isAllowedLoginKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.ctrlKey || event.metaKey || event.altKey) return true;
    if (event.key.length > 1) return true;

    return LOGIN_ALLOWED_CHARACTERS.test(event.key);
  }

  const scheduleLoginValidation = useCallback(
    (value: string) => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (!hasLoginValidation) return;

      const loginToValidate = value.trim();

      if (!loginToValidate || !hasLoginMinLength(loginToValidate)) {
        clearLoginFeedback();
        updateLoginValidationStatus('idle');
        return;
      }

      const loginValidationError = getLoginValidationError(loginToValidate);

      if (loginValidationError) {
        setLoginFeedback(loginValidationError);
        updateLoginValidationStatus('invalid');
        return;
      }

      clearLoginFeedback();
      updateLoginValidationStatus('idle');

      validationTimeoutRef.current = setTimeout(async () => {
        const validateLoginFn = validateLoginRef.current;
        if (!validateLoginFn || requestIdRef.current !== requestId) return;

        updateLoginValidationStatus('validating');

        try {
          const result = await validateLoginFn(loginToValidate);
          const fieldLogin = getLoginValue(form.getFieldValue('login')).trim();

          if (
            requestIdRef.current !== requestId ||
            fieldLogin !== loginToValidate
          ) {
            return;
          }

          if (result.isValid) {
            setLoginFeedback(LOGIN_AVAILABLE_MESSAGE);
            updateLoginValidationStatus('valid');
            return;
          }

          setLoginFeedback(
            result.reason || 'Login inválido. Por favor, insira outro login.',
          );
          updateLoginValidationStatus('invalid');
        } catch {
          const fieldLogin = getLoginValue(form.getFieldValue('login')).trim();

          if (
            requestIdRef.current !== requestId ||
            fieldLogin !== loginToValidate
          ) {
            return;
          }

          setLoginFeedback(
            'Não foi possível validar o login. Tente novamente.',
          );
          updateLoginValidationStatus('invalid');
        }
      }, loginValidationDebounceMs);
    },
    [
      clearLoginFeedback,
      form,
      hasLoginValidation,
      loginValidationDebounceMs,
      updateLoginValidationStatus,
    ],
  );

  const handleLoginChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      scheduleLoginValidation(event.target.value);
    },
    [scheduleLoginValidation],
  );

  const handleLoginKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!isAllowedLoginKey(event)) {
        event.preventDefault();
      }
    },
    [],
  );

  const loginSuffix = hasLoginValidation ? (
    loginValidationStatus === 'validating' ? (
      <LoadingOutlined />
    ) : loginValidationStatus === 'valid' ? (
      <CheckCircleTwoTone twoToneColor="#52c41a" />
    ) : undefined
  ) : undefined;

  function getLoginExtra(
    hasLoginValidation: boolean,
    status: RegisterAccountLoginValidationStatus,
    feedback?: string,
  ): ReactNode {
    if (!hasLoginValidation) return undefined;

    return (
      <Space
        className="register-account-form__login-extra"
        direction="vertical"
        size={0}
      >
        <Typography.Text type="secondary">{LOGIN_HELP_TEXT}</Typography.Text>

        {!!feedback && (
          <Typography.Text type={getLoginFeedbackType(status)}>
            {feedback}
          </Typography.Text>
        )}
      </Space>
    );
  }

  return (
    <Row style={{ width: '100%' }} gutter={[16, 12]}>
      <Col span={24}>
        <FloatingFormItem
          label={intl.formatMessage({
            id: 'register.form.account.login.label',
          })}
          name="login"
          extra={getLoginExtra(
            hasLoginValidation,
            loginValidationStatus,
            loginFeedback,
          )}
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'register.form.account.login.message',
              }),
            },
            {
              validator(_, value) {
                const loginValidationError = getLoginValidationError(value);

                if (loginValidationError) {
                  return Promise.reject(new Error(loginValidationError));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            maxLength={LOGIN_MAX_LENGTH}
            suffix={
              hasLoginValidation ? (
                <span style={{ display: 'inline-flex', width: 16 }}>
                  {loginSuffix}
                </span>
              ) : undefined
            }
            onChange={handleLoginChange}
            onKeyDown={handleLoginKeyDown}
          />
        </FloatingFormItem>
      </Col>

      <Col span={24}>
        <FloatingFormItem
          label={intl.formatMessage({
            id: 'register.form.account.password.label',
          })}
          name="password"
          extra={
            <Typography.Text type="secondary">
              {PASSWORD_HELP_TEXT}
            </Typography.Text>
          }
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'register.form.account.password.message',
              }),
            },
            {
              pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).{8,}$/,
              message: intl.formatMessage({
                id: 'register.form.account.password.message.valid',
              }),
            },
          ]}
        >
          <Input.Password style={{ width: '100%' }} />
        </FloatingFormItem>
      </Col>

      <Col span={24}>
        <FloatingFormItem
          label={intl.formatMessage({
            id: 'register.form.account.passwordVerify.label',
          })}
          name="passwordVerify"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'register.form.account.passwordVerify.message',
              }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: 'register.form.account.passwordVerify.message.valid',
                    }),
                  ),
                );
              },
            }),
          ]}
        >
          <Input.Password style={{ width: '100%' }} />
        </FloatingFormItem>
      </Col>
    </Row>
  );
}
